import { useContext, useEffect, useState } from "react"
import { DateTime } from "luxon"
import { lessonsContext } from "../providers/lessons.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { profileContext } from "../providers/profile.provider"
import { getLessonsInRange } from "../utils/firestore.utils"
import { isPlaceholderPartecipant } from "../utils/func.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"
import { isMatch } from "../utils/match.utils"
import {
	accumulateEarnings,
	emptyEarnings,
	finalizeEarnings,
	getLessonHours,
	getSchoolRates,
	isAssistantIn,
	isCoachIncome,
	lacksPaymentMethod,
} from "../utils/billing.utils"

export function useProfileStats(summaryDate) {
	const { coach, displayedCoachID } = useContext(profileContext)
	const { selectedSchool, getSelectedSchool, getSchoolSettings } = useContext(schoolContext)
	const { lessons, setLessons } = useContext(lessonsContext)
	const { paymentMethods } = useContext(sharedDataContext)

	const [lessonsTypeCount, setLessonsTypeCount] = useState(null)
	const [courtsUsage, setCourtsUsage] = useState(null)
	const [lessonsPaymentAmountByMethod, setLessonsPaymentAmountByMethod] = useState(null)
	const [earnings, setEarnings] = useState(null)
	const [playersLessonCount, setPlayersLessonCount] = useState(null)
	const [periodLessons, setPeriodLessons] = useState(null)
	const [lessonsWithoutPaymentMethod, setLessonsWithoutPaymentMethod] = useState(null)
	const [selectedTimeSlot, setSelectedTimeSlot] = useState(
		getSelectedSchool()?.courtTimeSlots ? getSelectedSchool()?.courtTimeSlots[0] : null,
	)

	const accumulateLessonHours = (acc, lesson) => {
		if (lesson.schoolID !== selectedSchool) return
		const hours = getLessonHours(lesson)
		if (hours <= 0) return
		const sportID = lesson.sportID || "unknownSport"
		if (!acc[sportID]) acc[sportID] = {}
		acc[sportID][lesson.lessonType] = (acc[sportID][lesson.lessonType] ?? 0) + hours
	}

	// elenco delle persone a cui è stata fatta lezione nel periodo
	const accumulatePlayers = (acc, lesson) => {
		if (lesson.schoolID !== selectedSchool) return
		if (!lesson.partecipants) return

		const hours = getLessonHours(lesson)
		lesson.partecipants.forEach(player => {
			// un posto ancora senza nome non è una persona a cui si è fatto lezione
			if (isPlaceholderPartecipant(player)) return
			const playerID = player.playerID || player.playerName || "unknownPlayer"
			const entry = acc[playerID] ?? {
				playerName: player.playerName ?? "Non specificato",
				lessons: 0,
				hours: 0,
				absences: 0,
				amount: 0,
			}

			if (player.isPresent === false) entry.absences += 1
			else {
				entry.lessons += 1
				entry.hours += hours
			}
			// quello che ha pagato l'ha incassato il titolare
			if (!isAssistantIn(lesson, displayedCoachID))
				entry.amount += Number(player.paymentAmount) || 0

			acc[playerID] = entry
		})
	}

	const accumulatePayments = (acc, lesson) => {
		if (lesson.schoolID !== selectedSchool) return
		if (!lesson.partecipants) return
		// gli allievi pagano il titolare, non chi lo assiste
		if (isAssistantIn(lesson, displayedCoachID)) return
		lesson.partecipants.forEach(player => {
			// quello che un sostituto paga per conto del circolo non è un
			// incasso tuo, e nemmeno quello che un allievo ha pagato al circolo
			if (!isCoachIncome(player, lesson)) return
			if (!(player.paymentMethod in acc)) acc[player.paymentMethod] = 0
			// non `parseInt`: un importo con i centesimi — e da quando il campo
			// si scala dal prezzo ce ne sono — non va troncato
			acc[player.paymentMethod] += Number(player.paymentAmount) || 0
		})
	}

	const computeCourtsUsage = timeSlot => {
		const schoolLessons = lessons?.[selectedSchool]
		if (!schoolLessons) return {}
		const result = {}
		const seen = new Set()

		Object.values(schoolLessons).forEach(dateLessons => {
			Object.entries(dateLessons).forEach(([coachID, lessonsArray]) => {
				if (displayedCoachID !== "Tutti" && coachID !== displayedCoachID) return
				if (!Array.isArray(lessonsArray)) return
				lessonsArray.forEach(lesson => {
					if (lesson.schoolID !== selectedSchool) return
					if (lesson.coachID !== displayedCoachID) return
					if (isCancelledLesson(lesson)) return
					const start = DateTime.fromJSDate(lesson.lessonStart?.toDate())
					const end = DateTime.fromJSDate(lesson.lessonEnd?.toDate())
					const key = `${coachID}_${start.toISO()}_${end.toISO()}`
					if (seen.has(key)) return
					seen.add(key)
					if (
						end < summaryDate.start.startOf("day") ||
						start > summaryDate.end.endOf("day")
					)
						return

					let effectiveStart = start
					let effectiveEnd = end

					if (timeSlot) {
						const slotStart = start.set({ hour: timeSlot.start, minute: 0 })
						const slotEnd = start.set({ hour: timeSlot.end, minute: 0 })
						const overlapStart = DateTime.max(start, slotStart)
						const overlapEnd = DateTime.min(end, slotEnd)
						if (overlapEnd <= overlapStart) return
						effectiveStart = overlapStart
						effectiveEnd = overlapEnd
					}

					if (lesson.court) {
						const { courtsSport, courtNumber } = lesson.court
						if (courtNumber !== 0) {
							if (!result[courtsSport]) result[courtsSport] = {}
							if (result[courtsSport][courtNumber] === undefined)
								result[courtsSport][courtNumber] = 0
							result[courtsSport][courtNumber] +=
								effectiveEnd.diff(effectiveStart, "hours").toObject().hours || 0
						}
					}
				})
			})
		})
		return result
	}

	// Effect 1: fetch lezioni per il periodo selezionato
	useEffect(() => {
		if (!selectedSchool || !summaryDate?.start || !summaryDate?.end || !displayedCoachID) return

		getSelectedSchool()?.courtTimeSlots
			? setSelectedTimeSlot(getSelectedSchool()?.courtTimeSlots[0])
			: setSelectedTimeSlot(null)

		const days = []
		let cursor = summaryDate.start.startOf("day")
		while (cursor <= summaryDate.end.endOf("day")) {
			days.push(cursor)
			cursor = cursor.plus({ days: 1 })
		}

		// Scrive sempre nel bucket della scuola a cui la lezione appartiene,
		// altrimenti cambiando circolo il riepilogo continua a leggere i dati
		// del circolo precedente. I giorni senza lezioni vanno scritti vuoti:
		// se li si salta restano in cache quelli del periodo di prima.
		const writePeriod = lessonsByDate =>
			setLessons(prev => {
				const schoolLessons = { ...(prev[selectedSchool] || {}) }
				days.forEach(dt => {
					const dateStr = dt.toISODate()
					schoolLessons[dateStr] = {
						...(schoolLessons[dateStr] || {}),
						[displayedCoachID]: lessonsByDate[dateStr] || [],
					}
				})
				return { ...prev, [selectedSchool]: schoolLessons }
			})

		const unsubscribe = getLessonsInRange(
			displayedCoachID,
			selectedSchool,
			summaryDate.start.toJSDate(),
			summaryDate.end.toJSDate(),
			writePeriod,
		)

		// senza questo i listener del circolo precedente restano attivi e
		// continuano a riscrivere lo state
		return () => unsubscribe()
	}, [selectedSchool, summaryDate, displayedCoachID])

	// Effect 2: calcola statistiche dalle lezioni della scuola selezionata
	useEffect(() => {
		const schoolLessons = lessons?.[selectedSchool]

		// cambiando circolo i riepiloghi vanno azzerati subito, in attesa dei
		// dati nuovi, altrimenti restano a video i numeri del circolo di prima
		if (!schoolLessons) {
			setLessonsTypeCount(null)
			setLessonsPaymentAmountByMethod(null)
			setPlayersLessonCount(null)
			setPeriodLessons(null)
			setLessonsWithoutPaymentMethod(null)
			setEarnings(null)
			return
		}

		const hoursAcc = {}
		const paymentsAcc = Object.keys(paymentMethods).reduce((acc, method) => {
			acc[method] = 0
			return acc
		}, {})

		// riepilogo economico: solo sul proprio profilo e se ci sono le tariffe
		const rates = getSchoolRates(coach?.billingData, selectedSchool)
		const showEarnings = !!coach && displayedCoachID === coach.coachID && !!rates
		// il riepilogo si calcola solo sul proprio profilo, quindi le tariffe
		// del campo sono per forza quelle di chi sta guardando
		const earningsContext = {
			rates,
			courtsFee: getSchoolSettings().courtsFee,
			coachID: displayedCoachID,
		}
		const earningsAcc = emptyEarnings()
		const playersAcc = {}
		// Le stesse lezioni da cui escono i numeri: servono per aprirle
		// dal riepilogo, che altrimenti dice quante sono senza poterle
		// mostrare.
		const periodAcc = []

		// una lezione arriva anche due volte (bucket del titolare e bucket
		// dell'assistente): la conto una volta sola, per lezione e non per
		// orario, altrimenti due lezioni diverse dello stesso coach alla stessa
		// ora si annullano a vicenda e una sparisce dal riepilogo
		const lessonSeen = new Set()

		Object.values(schoolLessons).forEach(dateLessons => {
			Object.entries(dateLessons).forEach(([coachID, lessonsArray]) => {
				if (displayedCoachID !== "Tutti" && coachID !== displayedCoachID) return
				if (!Array.isArray(lessonsArray)) return
				lessonsArray.forEach(lesson => {
					const start = DateTime.fromJSDate(lesson.lessonStart?.toDate())
					const end = DateTime.fromJSDate(lesson.lessonEnd?.toDate())
					// una lezione senza orari validi non deve far saltare il
					// resto del riepilogo
					if (!start.isValid || !end.isValid) return
					if (
						end < summaryDate.start.startOf("day") ||
						start > summaryDate.end.endOf("day")
					)
						return
					const lessonKey =
						lesson.lessonID ?? `${lesson.coachID}_${start.toISO()}_${end.toISO()}`
					if (lessonSeen.has(lessonKey)) return
					lessonSeen.add(lessonKey)
					// una lezione annullata non si è fatta: le sue assenze le
					// conta la scheda della stagione, il resto non c'è
					if (isCancelledLesson(lesson)) return

					// una partita non è una lezione: nel riepilogo c'è solo
					// il suo campo, e l'uso dei campi la conta da sé
					if (isMatch(lesson)) {
						if (showEarnings && lesson.schoolID === selectedSchool)
							accumulateEarnings(earningsAcc, lesson, earningsContext)
						return
					}

					accumulateLessonHours(hoursAcc, lesson)
					accumulatePayments(paymentsAcc, lesson)
					accumulatePlayers(playersAcc, lesson)
					if (lesson.schoolID === selectedSchool) periodAcc.push(lesson)
					if (showEarnings && lesson.schoolID === selectedSchool)
						accumulateEarnings(earningsAcc, lesson, earningsContext)
				})
			})
		})

		setLessonsTypeCount(hoursAcc)
		setLessonsPaymentAmountByMethod(paymentsAcc)
		setPlayersLessonCount(playersAcc)
		// dalla più recente, come lo storico di un allievo
		periodAcc.sort((a, b) => b.lessonStart?.toDate() - a.lessonStart?.toDate())
		setPeriodLessons(periodAcc)
		// quelle che finirebbero sotto "Non specificato" nei pagamenti. Dove
		// sei assistente non sono soldi tuoi, e non tocca a te segnarli.
		setLessonsWithoutPaymentMethod(
			periodAcc.filter(
				lesson =>
					!isAssistantIn(lesson, displayedCoachID) && lacksPaymentMethod(lesson, rates),
			),
		)
		setEarnings(showEarnings ? finalizeEarnings(earningsAcc) : null)
	}, [lessons, summaryDate, displayedCoachID, paymentMethods, selectedSchool, coach])

	// Effect 3: calcola utilizzo campi (dipende anche da selectedTimeSlot)
	useEffect(() => {
		setCourtsUsage(computeCourtsUsage(selectedTimeSlot))
	}, [lessons, summaryDate, displayedCoachID, selectedTimeSlot, selectedSchool])

	return {
		lessonsTypeCount,
		periodLessons,
		lessonsWithoutPaymentMethod,
		courtsUsage,
		lessonsPaymentAmountByMethod,
		playersLessonCount,
		earnings,
		selectedTimeSlot,
		setSelectedTimeSlot,
	}
}
